import { useEffect } from 'react';

/**
 * Optimized drag and drop configuration
 * Improves performance by reducing unnecessary re-renders
 */
export const useDragDropConfig = () => {
  useEffect(() => {
    // Disable text selection during drag
    const handleDragStart = () => {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    };

    const handleDragEnd = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);

    return () => {
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, []);
};

/**
 * Custom drag layer configuration for better performance
 * Reduces re-renders by using a single drag preview
 */
export const dragDropOptions = {
  // Reduce drag preview delay for instant feedback
  delay: 0,
  
  // Use native drag preview for better performance
  captureDraggingState: false,
  
  // Optimize touch support
  enableTouchEvents: true,
  
  // Reduce scroll sensitivity for smoother dragging
  scrollAngleRanges: [
    { start: 30, end: 150 },
    { start: 210, end: 330 },
  ],
};

/**
 * Throttle function for drag events
 * Reduces the number of updates during drag
 */
export const throttleDrag = <T extends (...args: any[]) => any>(
  func: T,
  limit: number = 16 // ~60fps
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  let lastResult: ReturnType<T>;

  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      lastResult = func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
    return lastResult;
  };
};
